<template>
    <div class="bg-white border border-border rounded-lgr px-4 py-3 md:px-6 md:py-4">
        <form class="relative" @submit.prevent="handleSearch">
            <BaseSearchInput
                v-model="searchInput"
                aria-label="Search conversations"
                placeholder="Search past conversations, documents and uploads"
                class="pr-20"
                :disabled="isFetching"
                maxlength="1000"
                @keyup.escape="handleClear" />
            <div class="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                <button
                    v-if="hasSearched"
                    type="button"
                    aria-label="Refresh search"
                    title="Refresh search results"
                    :disabled="isFetching"
                    class="text-control-text hover:text-valence-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    @click="handleRefresh">
                    <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
                </button>
                <button
                    v-if="searchInput"
                    type="submit"
                    aria-label="Search"
                    title="Search"
                    :disabled="isFetching"
                    class="text-valence-primary-500 hover:text-valence-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    <i class="bi bi-arrow-right-circle-fill text-lg" aria-hidden="true"></i>
                </button>
            </div>
        </form>
        <div class="flex flex-wrap items-center gap-1.5 md:gap-2 mt-2 md:mt-3">
            <span v-if="visibleChips.length > 1" class="body-small text-gray-400 whitespace-nowrap">Search in:</span>
            <!-- FIXME: replace with BaseToggleGroup once the component has support for it -->
            <BaseButton
                v-for="chip in visibleChips"
                :key="chip.key"
                theme="neutral"
                size="sm"
                class="rounded-full scaffold-body-small"
                :class="activeFilters.has(chip.key) ? 'bg-toggle-option-selected' : ''"
                @click="toggleFilter(chip.key)">
                {{ chip.label }}
            </BaseButton>
            <span class="hidden hover-capable:inline-flex">
                <BaseTooltip :as-child="false" trigger-aria-label="Search info" content-class="max-w-100">
                    <template #trigger>
                        <span class="text-gray-500 details ml-0.5">
                            <i class="bi bi-info-circle" aria-hidden="true"></i>
                        </span>
                    </template>
                    <template #content>{{ tooltipContent }}</template>
                </BaseTooltip>
            </span>
            <BaseButton v-if="searchInput" theme="tertiary" size="sm" class="ml-auto" @click="handleClear">Clear search</BaseButton>
        </div>
    </div>

    <!-- Search loading state -->
    <div v-if="isFetching" class="flex flex-col justify-center items-center py-12 gap-3">
        <LoadingSpinner class="text-valence-primary-500 h-8 w-8" aria-label="Searching conversations" />
        <p class="text-gray-500 details">Searching conversations...</p>
    </div>

    <!-- HTTP/network error -->
    <BaseNotification v-else-if="error && hasSearched" theme="error" hide-dismiss text="Something went wrong. Please try again." />

    <!-- LLM error (HTTP 200 but search service failed internally) -->
    <BaseNotification v-else-if="hasSearched && metadata?.llm_error" theme="error" hide-dismiss text="Search is temporarily unavailable. Please try again." />

    <!-- Empty results after search -->
    <div v-else-if="hasSearched && groupedResults.length === 0" class="px-4 md:px-6 py-3 break-words">
        <p class="text-gray-500 details">
            No results for "{{ query }}"<template v-if="searchedDateRange"> &middot; Searched: {{ searchedDateRange }}</template>
            &middot;
            <button type="button" class="text-valence-primary-500 hover:underline" @click="handleClear">Clear</button>
        </p>
    </div>

    <!-- Search results -->
    <div v-else-if="hasSearched && groupedResults.length > 0" class="px-4 pt-2 pb-4 md:px-6 md:pt-4">
        <p class="details md:body-small text-gray-500 mb-0.5 md:mb-1 leading-relaxed">
            Found {{ groupedResults.length }} conversation{{ groupedResults.length !== 1 ? "s" : "" }}
            <span class="text-gray-400">({{ results.length }} match{{ results.length !== 1 ? "es" : "" }})</span>
            <span v-if="searchedDateRange" class="text-gray-400"> &middot; {{ searchedDateRange }}</span>
        </p>
        <p v-if="matchedDateRange" class="details text-gray-400 mb-2 md:mb-3">
            <span class="whitespace-nowrap">Newest: {{ matchedDateRange.newest }}</span>
            <span> &middot; </span>
            <span class="whitespace-nowrap">Oldest: {{ matchedDateRange.oldest }}</span>
        </p>
        <div class="space-y-3">
            <div v-for="group in groupedResults" :key="group.session.uuid">
                <!-- Session card: top result preview -->
                <a
                    :href="group.topResult.session.url"
                    class="block p-4 border border-gray-200 rounded-lg hover:border-valence-primary-500 hover:bg-valence-primary-50 transition-colors"
                    @click="handleResultClick(group.topResult, 0)">
                    <div class="flex flex-wrap justify-between items-start gap-1 mb-1">
                        <span class="body-small text-[#8C8C8C] flex items-center gap-1.5">
                            <span
                                class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-medium leading-none"
                                :class="getResultBadge(group.topResult.result_type).className">
                                <i :class="getResultBadge(group.topResult.result_type).icon" class="text-[10px]" aria-hidden="true"></i>
                                {{ getResultBadge(group.topResult.result_type).label }}
                            </span>
                            <span v-if="formatRelevance(group.topResult.relevance)">{{ formatRelevance(group.topResult.relevance) }}</span>
                        </span>
                        <span class="details text-gray-400 whitespace-nowrap">{{ formatDate(group.topResult.message.when) }}</span>
                    </div>
                    <h3 class="body-small-semibold text-gray-800 mb-1">{{ group.session.title || "Untitled" }}</h3>
                    <!-- eslint-disable-next-line vue/no-v-html -- content is escaped via escapeHtml before highlight insertion -->
                    <p class="body-small text-gray-600 line-clamp-2" v-html="getSnippetHtml(group.topResult.message.content, group.topResult.matched_phrase)"></p>
                    <span v-if="group.matchCount > 1" class="details text-gray-400 mt-1 inline-block">
                        +{{ group.matchCount - 1 }} more match{{ group.matchCount - 1 !== 1 ? "es" : "" }}
                    </span>
                </a>

                <!-- Expandable additional matches -->
                <div v-if="group.matchCount > 1">
                    <button
                        type="button"
                        class="details text-gray-400 hover:text-valence-primary-500 mt-1 ml-2 md:ml-4 transition-colors"
                        @click="toggleExpanded(group.session.uuid)">
                        {{ expandedSessions.has(group.session.uuid) ? "Hide" : "Show" }} {{ group.matchCount - 1 }} more
                    </button>
                    <div v-if="expandedSessions.has(group.session.uuid)" class="ml-2 md:ml-4 mt-2 space-y-2 border-l-2 border-gray-100 pl-2 md:pl-3">
                        <a
                            v-for="(item, idx) in group.results.slice(1)"
                            :key="item.message.id"
                            :href="item.session.url"
                            class="block p-3 border border-gray-100 rounded hover:border-valence-primary-300 hover:bg-valence-primary-50/50 transition-colors"
                            @click="handleResultClick(item, idx + 1)">
                            <div class="flex flex-wrap items-center gap-1.5 mb-0.5">
                                <span
                                    class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-medium leading-none"
                                    :class="getResultBadge(item.result_type).className">
                                    <i :class="getResultBadge(item.result_type).icon" class="text-[10px]" aria-hidden="true"></i>
                                    {{ getResultBadge(item.result_type).label }}
                                </span>
                                <span class="details text-gray-400 whitespace-nowrap">{{ formatDate(item.message.when) }}</span>
                            </div>
                            <!-- eslint-disable-next-line vue/no-v-html -- content is escaped via escapeHtml before highlight insertion -->
                            <p class="body-small text-gray-600 line-clamp-2" v-html="getSnippetHtml(item.message.content, item.matched_phrase)"></p>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import BaseSearchInput from "~vue/components/form/BaseSearchInput.vue";
import { useConversationSearch } from "~vue/composables/useConversationSearch";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { computed, onMounted, ref, watch } from "vue";

const props = defineProps({
    documentUploadEnabled: { type: Boolean, default: false },
    documentCreatorEnabled: { type: Boolean, default: false },
    nadia2Enabled: { type: Boolean, default: false },
});

const searchInput = ref("");
const expandedSessions = ref(new Set());
const { query, results, metadata, groupedResults, isFetching, error, hasSearched, search, logResultClicked, reset, hydrate } = useConversationSearch();

const searchedDateRange = computed(() => {
    if (!metadata.value?.date_range_oldest) {
        return null;
    }
    const oldest = formatDateFull(metadata.value.date_range_oldest);
    const newest = metadata.value.date_range_newest ? formatDateFull(metadata.value.date_range_newest) : null;
    if (!newest || oldest === newest) {
        return oldest;
    }
    return `${oldest} – ${newest}`;
});

const matchedDateRange = computed(() => {
    if (results.value.length === 0) {
        return null;
    }
    let oldest = results.value[0].message.when;
    let newest = oldest;
    for (const r of results.value) {
        if (r.message.when < oldest) {
            oldest = r.message.when;
        }
        if (r.message.when > newest) {
            newest = r.message.when;
        }
    }
    return {
        oldest: formatDateFull(oldest),
        newest: formatDateFull(newest),
    };
});

const ALL_FILTER_CHIPS = [
    { key: "chat", label: "Chats", roles: ["user", "assistant"], purposes: ["chat"], sources: ["v1"], alwaysVisible: true },
    {
        key: "uploaded_docs",
        label: "Uploaded documents",
        roles: ["user"],
        purposes: ["file_upload", "pasted_content"],
        sources: ["v1"],
        flagProp: "documentUploadEnabled",
    },
    {
        key: "created_docs",
        label: "Created documents",
        roles: ["user", "assistant", "tool"],
        purposes: ["chat"],
        contentFilter: "document_output",
        sources: ["v1"],
        flagProp: "documentCreatorEnabled",
    },
    {
        key: "nadia2",
        label: "Nadia 2.0 chats",
        roles: ["user", "assistant"],
        purposes: ["chat"],
        sources: ["v2"],
        flagProp: "nadia2Enabled",
    },
];

const visibleChips = computed(() => ALL_FILTER_CHIPS.filter((c) => c.alwaysVisible || props[c.flagProp]));

const tooltipContent = computed(() => {
    const docParts = [];
    if (props.documentUploadEnabled) {
        docParts.push("uploaded");
    }
    if (props.documentCreatorEnabled) {
        docParts.push("created");
    }
    const docClause = docParts.length ? `, or for documents that were ${docParts.join(" or ")}` : "";
    return `Search for keywords in your past conversations${docClause}. Custom date ranges and full history search coming soon.`;
});

const activeFilters = ref(new Set(["chat"]));

function toggleFilter(key) {
    const next = new Set(activeFilters.value);
    if (next.has(key)) {
        if (next.size > 1) {
            next.delete(key);
        } else {
            return;
        }
    } else {
        next.add(key);
    }
    activeFilters.value = next;
    if (hasSearched.value && query.value) {
        search(query.value, resolveFilterOptions());
    }
}

function resolveFilterOptions() {
    if (activeFilters.value.size === 1 && activeFilters.value.has("chat")) {
        return {};
    }
    const roles = new Set();
    const purposes = new Set();
    const sources = new Set();
    let contentFilter = undefined;
    for (const chip of visibleChips.value) {
        if (activeFilters.value.has(chip.key)) {
            chip.roles.forEach((r) => roles.add(r));
            chip.purposes.forEach((p) => purposes.add(p));
            chip.sources?.forEach((s) => sources.add(s));
            if (chip.contentFilter) {
                contentFilter = chip.contentFilter;
            }
        }
    }
    const opts = { roles: [...roles], purposes: [...purposes] };
    if (sources.size) {
        opts.sources = [...sources];
    }
    if (contentFilter && activeFilters.value.size === 1) {
        opts.content_filter = contentFilter;
    }
    return opts;
}

onMounted(() => {
    if (hydrate()) {
        searchInput.value = query.value;
    }
});

async function handleSearch() {
    const trimmed = searchInput.value.trim();
    if (!trimmed || isFetching.value) {
        return;
    }
    expandedSessions.value.clear();
    await search(trimmed, resolveFilterOptions());
}

async function handleRefresh() {
    if (isFetching.value) {
        return;
    }
    const trimmed = searchInput.value.trim();
    if (!trimmed) {
        return;
    }
    await search(trimmed, resolveFilterOptions());
}

function handleClear() {
    searchInput.value = "";
    expandedSessions.value.clear();
    reset();
}

function toggleExpanded(uuid) {
    if (expandedSessions.value.has(uuid)) {
        expandedSessions.value.delete(uuid);
    } else {
        expandedSessions.value.add(uuid);
    }
}

function handleResultClick(item, position) {
    logResultClicked(item, position);
}

watch(searchInput, (newVal) => {
    if (!newVal && hasSearched.value) {
        reset();
    }
});

const RESULT_TYPE_BADGES = {
    chat: { label: "Chat", icon: "bi-chat-dots", className: "text-blue-600 bg-blue-50" },
    uploaded_doc: { label: "Uploaded doc", icon: "bi-file-earmark-arrow-up", className: "text-amber-600 bg-amber-50" },
    created_doc: { label: "Created doc", icon: "bi-file-earmark-text", className: "text-emerald-600 bg-emerald-50" },
    tool_output: { label: "Tool output", icon: "bi-gear", className: "text-purple-600 bg-purple-50" },
};

function getResultBadge(resultType) {
    return RESULT_TYPE_BADGES[resultType] || RESULT_TYPE_BADGES.chat;
}

function formatRelevance(relevance) {
    if (relevance >= 9) {
        return "Best match";
    }
    if (relevance >= 7) {
        return "Good match";
    }
    if (relevance >= 5) {
        return "Related";
    }
    return "";
}

function extractDocContent(raw) {
    // Created docs are stored as Python dict repr: {'document': {'content': '...', 'title': '...'}}
    // or JSON: {"document": {"content": "...", "title": "..."}}
    const patterns = [/['"]content['"]\s*:\s*['"](.+?)['"]\s*[,}]/s, /['"]title['"]\s*:\s*['"](.+?)['"]\s*[,}]/s];
    for (const pattern of patterns) {
        const match = raw.match(pattern);
        if (match) {
            return match[1].replace(/\\n/g, " ").replace(/\\'/g, "'");
        }
    }
    return null;
}

function getSnippetHtml(content, matchedPhrase) {
    // Clean up raw document blobs before displaying
    if (content.startsWith("{'document':") || content.startsWith('{"document":')) {
        const extracted = extractDocContent(content);
        if (extracted) {
            content = extracted;
        }
    }

    if (!matchedPhrase) {
        const escaped = escapeHtml(content);
        return escaped.length <= 150 ? escaped : escaped.slice(0, 150) + "...";
    }

    const lowerContent = content.toLowerCase();
    const lowerPhrase = matchedPhrase.toLowerCase();
    const index = lowerContent.indexOf(lowerPhrase);

    if (index === -1) {
        const escaped = escapeHtml(content);
        return escaped.length <= 150 ? escaped : escaped.slice(0, 150) + "...";
    }

    const contextChars = 60;
    const start = Math.max(0, index - contextChars);
    const end = Math.min(content.length, index + matchedPhrase.length + contextChars);

    const before = escapeHtml(content.slice(start, index));
    const phrase = escapeHtml(content.slice(index, index + matchedPhrase.length));
    const after = escapeHtml(content.slice(index + matchedPhrase.length, end));

    const prefix = start > 0 ? "..." : "";
    const suffix = end < content.length ? "..." : "";

    return `${prefix}${before}<mark class="bg-yellow-200 px-0.5 rounded">${phrase}</mark>${after}${suffix}`;
}

function escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
}

function formatDateFull(isoDate) {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) {
        return isoDate;
    }
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatDate(dateStr) {
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) {
            return dateStr;
        }
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return "Today";
        }
        if (diffDays === 1) {
            return "Yesterday";
        }
        if (diffDays < 7) {
            return `${diffDays} days ago`;
        }

        return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
        return dateStr;
    }
}

const isSearchActive = computed(() => hasSearched.value);
defineExpose({ hasSearched: isSearchActive });
</script>
